/**
 * Client-side check for TruInsight card links.
 *
 * The server builds every href. This rejects anything that is not on that catalog before it
 * becomes a router link, including a card restored from an older saved conversation.
 */
import type { AiCard, AiCardFact } from '../types';

const CARD_TYPES = new Set(['building', 'lease', 'order', 'report', 'metric', 'list', 'page']);

/** Router paths TruInsight is allowed to open. No origin, no other routes. */
const INSIGHT_HREF =
  /^\/(?:properties\/\d+(?:\?tab=(?:stacking-plan|floors-tenants|reports|documents|category-summary|value-opportunity|drawings))?|property-dashboard\/\d+|reports\/\d+|orders\/\d+)$/;

/** Documents tab opened on one folder. Param order is fixed so a saved link cannot smuggle extras. */
const DOCUMENT_FOLDER_HREF = /^\/properties\/\d+\?tab=documents&folderId=\d+$/;

/** Drawings tab opened on one vault scope, optionally one floor. */
const DRAWING_FLOOR_HREF =
  /^\/properties\/\d+\?tab=drawings&scope=(?:suite_file|floor_file|core_shell|parking|landscaping)(?:&floorId=\d+)?$/;

/** Floors & Tenants with the Space Accounting Book sheet open. */
const SAB_SHEET_HREF = /^\/properties\/\d+\?tab=floors-tenants(?:&reportId=\d+)?&sab=1$/;

/** `open_page` destinations (server catalog: `server/src/features/ai/ai.page-links.ts`). */
const PAGE_HREFS: readonly RegExp[] = [
  /^\/(?:dashboard|alerts|support|drawing-vault|properties)$/,
  /^\/reports\?tab=(?:measurements|category-summary|value-opportunity|audit-trail|tenant-activity)$/,
  /^\/teams(?:\?tab=(?:all|internal|external|partners|pending))?$/,
  /^\/properties\/\d+\?tab=report-updates$/,
  /^\/properties\/\d+\?tab=(?:drawings|team)$/,
  /^\/reports\/\d+\/(?:details|compare\/\d+)$/,
  /^\/orders\/new(?:\?buildingId=\d+)?$/,
];

/** Filtered list links (`/orders?…`, `/properties?…`). Each page accepts only its own filter keys. */
const LIST_HREF = /^\/(orders|properties)\?([^#]*)$/;

const ID_LIST = /^\d+(?:,\d+)*$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_TEXT_PARAM = 255;

type ParamCheck = (value: string) => boolean;

const isText: ParamCheck = (value) => value.trim() !== '' && value.length <= MAX_TEXT_PARAM;

const LIST_PARAMS: Record<'orders' | 'properties', Record<string, ParamCheck>> = {
  orders: {
    view: (value) => value === 'active' || value === 'inactive' || value === 'all',
    q: isText,
    status: isText,
    serviceType: isText,
    dateFrom: (value) => ISO_DATE.test(value),
    dateTo: (value) => ISO_DATE.test(value),
    sort: (value) => /^-?[A-Za-z]+$/.test(value),
  },
  properties: {
    q: isText,
    buildingTypeIds: (value) => ID_LIST.test(value),
    reportTypeIds: (value) => ID_LIST.test(value),
  },
};

function isSafeListHref(href: string): boolean {
  const match = LIST_HREF.exec(href);
  if (!match) return false;
  const allowed = LIST_PARAMS[match[1] as 'orders' | 'properties'];
  const params = new URLSearchParams(match[2]);
  let count = 0;
  for (const [key, value] of params) {
    const check = allowed[key];
    if (!check || !check(value)) return false;
    count += 1;
  }
  return count > 0;
}

/** True when `href` is one of the TruInsight catalog paths. */
export function isSafeInsightHref(href: string): boolean {
  return (
    INSIGHT_HREF.test(href) ||
    DOCUMENT_FOLDER_HREF.test(href) ||
    DRAWING_FLOOR_HREF.test(href) ||
    SAB_SHEET_HREF.test(href) ||
    PAGE_HREFS.some((pattern) => pattern.test(href)) ||
    isSafeListHref(href)
  );
}

const MAX_INLINE_LINKS = 25;
const MAX_LINK_LABEL = 180;

/** Names the answer may turn into links. Drops anything off the catalog. */
export function sanitizeAiLinks(raw: unknown): { label: string; href: string }[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const links: { label: string; href: string }[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const label = (item as { label?: unknown }).label;
    const href = (item as { href?: unknown }).href;
    if (typeof label !== 'string' || typeof href !== 'string') continue;
    const trimmed = label.trim();
    if (trimmed.length < 2 || trimmed.length > MAX_LINK_LABEL || !isSafeInsightHref(href)) continue;
    links.push({ label: trimmed, href });
    if (links.length >= MAX_INLINE_LINKS) break;
  }
  return links.length > 0 ? links : undefined;
}

/**
 * Keep only well-formed cards. Drops a card whose link is not on the catalog rather than
 * rendering it as a plain card with a bad path — the whole card is skipped if the title is
 * missing; a bad href is removed and the card stays informational.
 */
export function sanitizeAiCards(raw: unknown): AiCard[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const cards: AiCard[] = [];
  for (const item of raw) {
    const card = sanitizeCard(item);
    if (card) cards.push(card);
    if (cards.length >= 5) break;
  }
  return cards.length > 0 ? cards : undefined;
}

function sanitizeCard(raw: unknown): AiCard | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Partial<AiCard>;
  if (typeof item.type !== 'string' || !CARD_TYPES.has(item.type)) return null;
  if (typeof item.title !== 'string' || item.title.trim() === '') return null;
  const facts = sanitizeFacts(item.facts);
  const href = typeof item.href === 'string' && isSafeInsightHref(item.href) ? item.href : undefined;
  const actionLabel =
    href && typeof item.actionLabel === 'string' && item.actionLabel.trim() !== ''
      ? item.actionLabel
      : undefined;
  return {
    type: item.type as AiCard['type'],
    title: item.title,
    facts,
    ...(href ? { href, actionLabel } : {}),
  };
}

function sanitizeFacts(raw: unknown): AiCardFact[] {
  if (!Array.isArray(raw)) return [];
  const facts: AiCardFact[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const fact = item as Partial<AiCardFact>;
    if (typeof fact.label !== 'string' || typeof fact.value !== 'string') continue;
    if (!fact.label.trim() || !fact.value.trim()) continue;
    facts.push({ label: fact.label, value: fact.value });
    if (facts.length >= 3) break;
  }
  return facts;
}
